import {
  Alert,
  AlertTitle,
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import { FormikErrors } from "formik";
import { chain } from "lodash-es";
import { enqueueSnackbar } from "notistack";
import { ReactNode, Ref, forwardRef, useImperativeHandle, useMemo, useState } from "react";

import AppButton from "@/common/components/Button/AppButton";
import AppIconButton from "@/common/components/Button/AppIconButton";
import { VehiclePartTypeAutocompleteProps } from "@/common/components/Entity/ReferenceData/VehiclePartType/VehiclePartTypeAutocomplete";
import VehicleDamageViewModal from "@/common/components/Entity/VehicleDamage/View/VehicleDamageViewModal";
import {
  VisualModelViewer,
  VisualModelViewerProps,
} from "@/common/components/Entity/VehicleVisualModel/Viewer/v2/VisualModelViewer";
import AppIcon from "@/common/components/Icons/AppIcon";
import ConfirmationModal from "@/common/components/Modals/ConfirmationModal";
import AppPopover from "@/common/components/Popover/AppPopover";
import AppPopoverContent from "@/common/components/Popover/AppPopoverContent";
import { VisualModelHelper } from "@/common/helpers/entity/vehicleVisualModel";
import { GenericDamageHelper } from "@/common/helpers/genericDamage";
import { IdHelper } from "@/common/helpers/id";
import { SvgHelper } from "@/common/helpers/svg";
import { TextHelper } from "@/common/helpers/text";
import { TypeHelper } from "@/common/helpers/type";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { useExactOrDefaultVehicleVisualModel } from "@/common/hooks/entity/vehicleVisualModel/useExactOrDefaultVehicleVisualModel";
import { Mapper } from "@/common/mapping/mapper";
import { ApiEnumName, enumService } from "@/common/services/enum";
import { cast } from "@/common/ts/conversions";
import { EntityFormMode } from "@/common/ts/entity";
import { GenericDamage, VisualModelPoint, VisualModelPointMeta } from "@/common/ts/visualModel";
import { apiClient } from "@/core/api/ApiClient";
import {
  VehicleArea,
  VehicleDamageDto,
  VehicleDamageGetPaginatedDto,
  VehicleDamageState,
  VehicleProjection,
  VehicleVisualModelDto,
} from "@/core/api/generated/v1";

import { VisualModelViewerOfVehicleDamagesModal } from "../Entity/VisualModelViewerOfVehicleDamagesModal";
import { GenericDamageList } from "./GenericDamageList";
import { GenericDamageViewModal } from "./GenericDamageViewModal";
import { VisualModelEditorOfGenericDamageInputModal } from "./VisualModelEditorOfGenericDamageInputModal";
import { VisualModelViewerOfGenericDamagesModal } from "./VisualModelViewerOfGenericDamagesModal";

// DD-specific points
interface DamagePointCurrent<TDamage extends GenericDamage> extends VisualModelPoint<
  GenericDamage,
  VisualModelPointMeta
> {
  type: "current";
  data: TDamage;
}

export interface DamagePointOld extends VisualModelPoint<
  GenericDamage<VehicleDamageDto>,
  VisualModelPointMeta
> {
  type: "old";
  data: GenericDamage<VehicleDamageDto>;
}

export type DamagePoint<TDamage extends GenericDamage> =
  | DamagePointOld
  | DamagePointCurrent<TDamage>;

export type PartTypeExtraFilters = Nil<
  VehiclePartTypeAutocompleteProps["searchFilters"] & {
    damageId: string;
  }
>;

export interface VehicleVisualModelGenericDamageEditorRefHandle<TDamage extends GenericDamage> {
  create: (damage: GenericDamage) => void;
  update: (damage: TDamage) => void;
  delete: (damage: TDamage) => void;
}

export const GenericDamageViewerMode = {
  View: "View",
  Edit: "Edit",
} as const;
export type GenericDamageViewerMode =
  (typeof GenericDamageViewerMode)[keyof typeof GenericDamageViewerMode];

export interface VisualModelEditorOfGenericDamageProps<TDamage extends GenericDamage> {
  mode: GenericDamageViewerMode;
  vehicleId: Nil<string>;
  visualModelId: Nil<string>;
  visualModel: Nil<VehicleVisualModelDto>;
  damages: TDamage[];
  errors?: Nil<Array<FormikErrors<TDamage> | undefined>>;
  /** Automatically pick primary visual model based on the damages.
   * Set this prop to `true` and `visualModel` to `null`;
   */
  isAutoPickVisualModel?: boolean;
  /** Display old/existing damage points excluding the current damages. */
  isDisplayOld?: boolean;
  /** Display damage list/table. */
  isDisplayList?: boolean;
  /** Can click and view old damage in a modal. */
  isViewOldEnabled?: boolean;
  /** Can click and view current damage in a modal. */
  isViewEnabled?: boolean;
  isCreateEnabled?: boolean;
  isUpdateEnabled?: boolean;
  isDeleteEnabled?: boolean;
  isMoveEnabled?: boolean;
  isSplitEnabled?: boolean;
  isMergeEnabled?: boolean;
  oldDamagesDisplay?: {
    /** States to display by default. Empty array - don't display by default.
     * @default [VehicleDamageState.Actual]
     */
    defaultStates?: VehicleDamageState[];
    /** States allowed to toggle in settings.
     * @default All
     */
    allowedStates?: VehicleDamageState[];
    /** Enable state show/hide toggles in settings.
     * @default false
     */
    isStateTogglesEnabled?: boolean;
  };
  createUpdateProps?: {
    isPartTypeDisabled?: boolean;
    isDamageTypeDisabled?: boolean;
    isSubmitDisabled?: boolean;
  };
  deleteProps?: {
    confirmationModal?: {
      bodyAfter: (damage: TDamage) => ReactNode;
    };
  };
  visualModelViewerProps?: Partial<VisualModelViewerProps>;
  /** If set, overrides default view behavior. */
  onView?: (damage: TDamage) => void;
  /** If set, overrides default view behavior. */
  onViewOld?: (damage: VehicleDamageDto) => void;
  onCreated: (newValue: GenericDamage) => void;
  /** Single damage updated.
   * If the provided damage is changed in this callback, return it for the immediate effect. */
  onUpdated: (newValue: TDamage) => void | { newValue: TDamage };
  onDeleted: (newValue: TDamage) => void;
  /** When N damages change (mix of created, updated and deleted).
   * E.g. on damage split or merge.
   * NB: Passes only changed damages, not all.
   */
  onManyChanged: (params: { added?: TDamage[]; updated?: TDamage[]; removed?: TDamage[] }) => void;
}

const logPrefix = "VehicleVisualModelGenericDamageEditor";

// forwardRef with TS generic component trick:
// https://stackoverflow.com/questions/58469229/react-with-typescript-generics-while-using-react-forwardref
type TheComponentWithForwardedRefType = <TDamage extends GenericDamage>(
  props: VisualModelEditorOfGenericDamageProps<TDamage> & {
    ref?: React.Ref<VehicleVisualModelGenericDamageEditorRefHandle<TDamage>>;
  },
) => ReturnType<typeof TheComponent>;

/** Generic viewer/editor for damage points on visual model. */
function TheComponent<TDamage extends GenericDamage>(
  {
    mode,
    vehicleId,
    visualModelId,
    visualModel,
    damages,
    errors,
    isAutoPickVisualModel = false,
    isDisplayOld = false,
    isDisplayList = true,
    isViewOldEnabled = false,
    isViewEnabled = true,
    isCreateEnabled = mode === GenericDamageViewerMode.Edit,
    isUpdateEnabled = mode === GenericDamageViewerMode.Edit,
    isDeleteEnabled = mode === GenericDamageViewerMode.Edit,
    isMoveEnabled = mode === GenericDamageViewerMode.Edit,
    isSplitEnabled = mode === GenericDamageViewerMode.Edit,
    isMergeEnabled = mode === GenericDamageViewerMode.Edit,
    oldDamagesDisplay,
    createUpdateProps,
    deleteProps,
    visualModelViewerProps,
    onView,
    onViewOld,
    onCreated,
    onUpdated,
    onDeleted,
    onManyChanged,
  }: VisualModelEditorOfGenericDamageProps<TDamage>,
  ref: Ref<VehicleVisualModelGenericDamageEditorRefHandle<TDamage>>,
) {
  // const isViewMode = mode === GenericDamageViewerMode.View;
  const isEditMode = mode === GenericDamageViewerMode.Edit;

  const [oldDamageToView, setOldDamageToView] = useState<Nil<VehicleDamageDto>>(null);
  const [damageToCreate, setDamageToCreate] = useState<Nil<GenericDamage>>(null);
  const [damageToView, setDamageToView] = useState<Nil<TDamage>>(null);
  const [damageToUpdate, setDamageToUpdate] = useState<Nil<TDamage>>(null);
  const [damageToDelete, setDamageToDelete] = useState<Nil<TDamage>>(null);
  const [partTypeExtraFilters, setPartTypeExtraFilters] = useState<PartTypeExtraFilters>(undefined);
  const [isViewDamagesOnOtherVisualModelsModalOpen, setIsViewDamagesOnOtherVisualModelsModalOpen] =
    useState(false);
  const [
    isViewOldDamagesOnOtherVisualModelsModalOpen,
    setIsViewOldDamagesOnOtherVisualModelsModalOpen,
  ] = useState(false);

  const damageToCreateOrUpdate = (damageToCreate || damageToUpdate) as TDamage;

  const currentVehicleDamageIds = useMemo(
    () => damages.map((x) => x.vehicleDamageId || "").filter(Boolean),
    [damages],
  );

  // pick visual model
  const visualModelIds = useMemo(
    () => damages.map((x) => x.visualModelId!).filter(Boolean),
    [damages],
  );
  const visualModelPickResult = useMemo(
    () =>
      isAutoPickVisualModel ? VisualModelHelper.pickForDamagesDisplay(visualModelIds) : undefined,
    [isAutoPickVisualModel, visualModelIds],
  );
  const visualModelResult = useExactOrDefaultVehicleVisualModel({
    isEnabled: !!visualModelId || isAutoPickVisualModel,
    visualModelId: visualModelId || visualModelPickResult?.visualModelIdPrimary,
    isFallbackToDefault: true,
    defaultModelFilters: {
      vehicleId: vehicleId,
    },
  });
  const visualModelComputed = visualModel || visualModelResult.visualModel;

  // old vehicle damages
  const oldDamagesDisplayComputed = useMemo<typeof oldDamagesDisplay>(
    () => ({
      defaultStates: [VehicleDamageState.Actual],
      isStateTogglesEnabled: false,
      allowedStates: enumService.getEnumValues(ApiEnumName.VehicleDamageState, {
        exceptNone: true,
      }),
      ...oldDamagesDisplay,
    }),
    [oldDamagesDisplay],
  );
  const [oldDamagesFetchFilters, setOldDamagesFetchFilters] =
    useState<VehicleDamageGetPaginatedDto>({
      offset: 0,
      limit: 200,
      states: oldDamagesDisplayComputed?.defaultStates,
    });
  const oldDamagesFetchFiltersComputed = useMemo<VehicleDamageGetPaginatedDto>(
    () => ({
      ...oldDamagesFetchFilters,
      statesAny: TypeHelper.isEmptyArray(oldDamagesFetchFilters.states)
        ? undefined
        : oldDamagesFetchFilters.states,
      excludeIds: currentVehicleDamageIds, // don't show current damages as existing
    }),
    [oldDamagesFetchFilters, currentVehicleDamageIds],
  );
  const isDisplayAnyOldDamage = TypeHelper.isNotEmptyArray(oldDamagesFetchFilters.states);

  // fetch old vehicle damages
  const paginatedOldVehicleDamagesRequest = useApiRequest(
    apiClient.vehicleDamagesApi.apiV1VehiclesVehicleIdDamagesGetPost,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      vehicleId: vehicleId || "",
      vehicleDamageGetPaginatedDto: {
        ...oldDamagesFetchFiltersComputed,
      },
    },
    {
      deps: [vehicleId, oldDamagesFetchFiltersComputed],
      skip: !vehicleId || !isDisplayOld || !isDisplayAnyOldDamage,
    },
  );
  const paginatedOldVehicleDamages =
    isDisplayOld && isDisplayAnyOldDamage ? paginatedOldVehicleDamagesRequest?.data : null;

  const vehicleDamagesOnOtherVisualModels = useMemo(
    () =>
      visualModelComputed
        ? paginatedOldVehicleDamages?.items?.filter(
            (x) => x.visualModel?.id !== visualModelComputed.id,
          ) || null
        : null,
    [visualModelComputed, paginatedOldVehicleDamages],
  );

  const damagesOld = useMemo<GenericDamage<VehicleDamageDto>[]>(
    () =>
      visualModelComputed
        ? paginatedOldVehicleDamages?.items
            ?.filter((x) => x.visualModel?.id === visualModelComputed.id)
            .map((x) => Mapper.visualModel.mapVehicleDamageDtoToGenericDamage(x)) || []
        : [],
    [visualModelComputed, paginatedOldVehicleDamages],
  );

  // for the primary visual model, distinguish native and foreign damages
  const damagesOnPrimaryVisualModel = useMemo(
    () =>
      visualModelComputed ? damages.filter((x) => x.visualModelId === visualModelComputed.id) : [],
    [visualModelComputed, damages],
  );
  const damagesOnForeignVisualModels = useMemo(
    () =>
      visualModelComputed ? damages.filter((x) => x.visualModelId !== visualModelComputed.id) : [],
    [visualModelComputed, damages],
  );

  const pointsOld = useMemo<DamagePointOld[]>(
    () =>
      damagesOld
        .map(
          (x) =>
            Mapper.visualModel.mapGenericDamageToVisualModelPoint(x, {
              style: { color: VisualModelHelper.svgPointColorMap.damageOld },
              isDraggable: false,
              meta: {
                type: "old",
              },
            })!,
        )
        .filter((x) => x && x.data)
        .map((x) =>
          cast<DamagePointOld>({
            ...x,
            style: {
              ...x.style,
              ...(x.data?.data
                ? VisualModelHelper.getSvgPointStyleForOldVehicleDamage(x.data.data)
                : {}),
            },
            type: "old",
            data: x.data!,
          }),
        ),
    [damagesOld],
  );

  const pointsCurrent = useMemo<DamagePointCurrent<TDamage>[]>(
    () =>
      damagesOnPrimaryVisualModel
        .map(
          (x) =>
            Mapper.visualModel.mapGenericDamageToVisualModelPoint<TDamage>(x, {
              isDraggable: isEditMode && isMoveEnabled,
              meta: {
                type: "current",
              },
            })!,
        )
        .filter((x) => x && x.data)
        .map((x) => cast<DamagePointCurrent<TDamage>>({ ...x, type: "current", data: x.data! })),
    [damagesOnPrimaryVisualModel, isEditMode, isMoveEnabled],
  );

  const points = useMemo<DamagePoint<TDamage>[]>(
    () => [...(pointsOld || []), ...(pointsCurrent || [])],
    [pointsOld, pointsCurrent],
  );

  const errorsByDamageIdMap = useMemo<Record<string, FormikErrors<TDamage> | undefined>>(
    () =>
      chain(damages.map((x, i) => ({ damage: x, index: i })) || [])
        .keyBy((x) => x.damage.id || "")
        .mapValues((x) => errors?.at(x.index) || undefined)
        .value(),
    [damages, errors],
  );

  useImperativeHandle(ref, () => ({
    create(damage) {
      setDamageToCreate(damage);
    },
    update(damage) {
      setDamageToUpdate(damage);
    },
    delete(damage) {
      setDamageToDelete(damage);
    },
  }));

  const view = (damage: TDamage) => {
    if (onView) {
      onView(damage);
    } else {
      setDamageToView(damage);
    }
  };

  const viewOld = (damage: VehicleDamageDto) => {
    if (onViewOld) {
      onViewOld(damage);
    } else {
      setOldDamageToView(damage);
    }
  };

  return (
    <Stack gap={1}>
      {/* Foreign damages */}
      {isEditMode && isMoveEnabled && TypeHelper.isNotEmptyArray(damagesOnForeignVisualModels) && (
        <Box>
          <Alert severity='warning'>
            <AlertTitle>
              Repositioning of {damagesOnForeignVisualModels.length}{" "}
              {TextHelper.pluralizeManual("damage", damagesOnForeignVisualModels.length, "damages")}{" "}
              is required.
            </AlertTitle>
            <Box>
              You have {damagesOnForeignVisualModels.length}{" "}
              {TextHelper.pluralizeManual("damage", damagesOnForeignVisualModels.length, "damages")}{" "}
              marked on other visual models.
            </Box>
            <Box>
              In order to correctly view and edit that damages here, you need to reposition them on
              the current visual model.
            </Box>
            <Box>
              To reposition, click <strong>Start</strong> and then <strong>Drag & Drop</strong>{" "}
              highlighted damages on the correct location.
            </Box>

            {visualModelComputed && (
              <AppButton
                sx={{ mt: 1 }}
                disabled={!isEditMode}
                variant='contained'
                color='primary'
                size='small'
                startIcon={<AppIcon of='start' />}
                onClick={() => {
                  // initially place points approximately in the top left corder in a row and let user to reposition manually
                  const radiusPct =
                    (VisualModelHelper.defaultSvgPointStyleForDamage.circleRadiusPct ?? 3) / 100;
                  const gapPct = 0.02;
                  const initialXp = radiusPct + gapPct;
                  const initialYp = radiusPct + gapPct;
                  const newForeignDamages = damagesOnForeignVisualModels.map((damage, i) =>
                    cast<TDamage>({
                      ...damage,
                      visualModelId: visualModelComputed.id,
                      point: damage.point
                        ? {
                            x: 0,
                            y: 0,
                            xp: Math.min(initialXp + initialXp * i, 0.99),
                            yp: initialYp,
                            width: 0,
                            height: 0,
                          }
                        : undefined,
                      isRepositioningRequired: true,
                    }),
                  );
                  onManyChanged({
                    updated: [...newForeignDamages],
                  });
                }}
              >
                Start
              </AppButton>
            )}
          </Alert>
        </Box>
      )}

      {/* Visual model */}
      <VisualModelViewer
        {...visualModelViewerProps}
        visualModelId={undefined}
        visualModel={visualModelComputed}
        points={points}
        initialMainImageSelector={
          visualModelViewerProps?.initialMainImageSelector || {
            isFirstWithPoints: true,
            area: VehicleArea.Exterior,
            projection: VehicleProjection.Front,
          }
        }
        isFetchVisualModel={false}
        slots={{
          ...visualModelViewerProps?.slots,
        }}
        mainViewerProps={{
          ...visualModelViewerProps?.mainViewerProps,
          svgViewerProps: {
            ...visualModelViewerProps?.mainViewerProps?.svgViewerProps,
            svgProps: {
              ...visualModelViewerProps?.mainViewerProps?.svgViewerProps?.svgProps,
              cursor: isEditMode && isCreateEnabled ? "crosshair" : "default",
              point: {
                ...visualModelViewerProps?.mainViewerProps?.svgViewerProps?.svgProps?.point,
                cursor:
                  isViewEnabled ||
                  (isEditMode && (isUpdateEnabled || isDeleteEnabled || isMoveEnabled))
                    ? "pointer"
                    : "default",
              },
            },
            isPointDragEnabled: isEditMode && isMoveEnabled,
          },
          renderCustomControls: !oldDamagesDisplayComputed?.isStateTogglesEnabled
            ? undefined
            : (params) => (
                <Stack>
                  <AppPopover
                    clickBehavior={{}}
                    trigger={
                      <AppIconButton
                        {...params.iconButtonProps}
                        tooltipProps={{
                          ...params.iconButtonProps.tooltipProps,
                          title: "Display settings",
                          enterDelay: 300,
                          enterNextDelay: 300,
                        }}
                      >
                        <AppIcon of='settings' />
                      </AppIconButton>
                    }
                  >
                    <AppPopoverContent sx={{ minWidth: { md: 300 } }}>
                      <Box>
                        {oldDamagesDisplayComputed?.isStateTogglesEnabled && (
                          <Stack gap={1}>
                            <Typography variant='subtitle1'>
                              Old damages to display{" "}
                              {paginatedOldVehicleDamagesRequest.isLoading && (
                                <CircularProgress size={16} />
                              )}
                            </Typography>

                            <Stack gap={0.5}>
                              {oldDamagesDisplayComputed?.allowedStates?.map((state) => (
                                <FormControl key={state} fullWidth margin='none'>
                                  <FormControlLabel
                                    sx={{ m: 0 }}
                                    label={`${enumService.getEnumValueName(ApiEnumName.VehicleDamageState, state)} damages`}
                                    control={
                                      <Checkbox
                                        sx={{ py: 0, pl: 0 }}
                                        checked={
                                          oldDamagesFetchFilters.states?.includes(state) ?? false
                                        }
                                        onChange={(e) => {
                                          setOldDamagesFetchFilters((prev) => {
                                            const newValue = {
                                              ...prev,
                                              states: e.target.checked
                                                ? [...(prev.states || []), state]
                                                : prev.states?.filter((x) => x !== state),
                                            };

                                            const isAnyEnabled = TypeHelper.isNotEmptyArray(
                                              newValue.states,
                                            );
                                            if (!isAnyEnabled) {
                                              paginatedOldVehicleDamagesRequest.replaceData(null);
                                            }

                                            return newValue;
                                          });
                                        }}
                                      />
                                    }
                                  />
                                </FormControl>
                              ))}
                            </Stack>
                          </Stack>
                        )}
                      </Box>
                    </AppPopoverContent>
                  </AppPopover>
                </Stack>
              ),
          onNewPointClicked: (params) => {
            if (!isEditMode || !isCreateEnabled) {
              return;
            }
            const newDamage: GenericDamage = {
              id: IdHelper.newTempFeId(),
              visualModelId: visualModelComputed?.id,
              vehicleDamageId: undefined,
              vehicleDamage: undefined,
              partTypeId: undefined,
              partType: undefined,
              partTypeRef: undefined,
              damageTypeId: undefined,
              damageType: undefined,
              damageTypeRef: undefined,
              area: params.area || undefined,
              projection: params.projection || undefined,
              point: SvgHelper.mapSvgPointFullCoordsToGeneralSvgPointDto(params.coords),
              notes: undefined,
              attachments: undefined,
              svgPathMetadata: params.svgPathMetadata,
              data: null,
            };
            console.log(logPrefix, "onNewPointClicked.", { params, newDamage });
            setPartTypeExtraFilters(
              newDamage.svgPathMetadata
                ? {
                    damageId: newDamage.id,
                    category: newDamage.svgPathMetadata?.vehiclePartCategory || undefined,
                    types: newDamage.svgPathMetadata?.vehiclePartTypes || undefined,
                    descriptors: newDamage.svgPathMetadata?.vehiclePartDescriptors || undefined,
                  }
                : undefined,
            );
            setDamageToCreate(newDamage);
          },
          onPointClicked: (params) => {
            console.log(logPrefix, "onPointClicked.", { params });

            if (
              params.point.type === "current" &&
              (isViewEnabled || isUpdateEnabled || isDeleteEnabled)
            ) {
              // setPartTypeExtraFilters(
              //   params.point.data.svgPathMetadata
              //     ? {
              //         category: params.point.data.svgPathMetadata?.vehiclePartCategory || undefined,
              //         types: params.point.data.svgPathMetadata?.vehiclePartTypes || undefined,
              //         descriptors:
              //           params.point.data.svgPathMetadata?.vehiclePartDescriptors || undefined,
              //       }
              //     : undefined,
              // );

              if (isEditMode && (isUpdateEnabled || isDeleteEnabled)) {
                setDamageToUpdate(params.point.data);
              } else if (isViewEnabled) {
                view(params.point.data);
              }
            } else if (
              params.point.type === "old" &&
              isViewOldEnabled &&
              params.point.data.vehicleDamage
            ) {
              viewOld(params.point.data.vehicleDamage);
            }
          },
          onPointMoved: (params) => {
            if (!isEditMode || !isMoveEnabled) {
              return;
            }
            if (params.point.type !== "current") {
              return;
            }

            const oldPartType =
              params.point.data.partType?.type ||
              params.point.data.partTypeRef?.type ||
              params.point.data.svgPathMetadata?.vehiclePartTypes?.at(0);
            const oldPartDescriptor =
              params.point.data.partType?.descriptor ||
              params.point.data.svgPathMetadata?.vehiclePartDescriptors?.at(0);

            const newPartType = params.svgPathMetadata?.vehiclePartTypes?.at(0);
            const newPartDescriptor = params.svgPathMetadata?.vehiclePartDescriptors?.at(0);

            const isAnyPartTypeSet = !!oldPartType || !!newPartType;
            const isAnyDescriptorSet = !!oldPartDescriptor || !!newPartDescriptor;
            const isPartTypeMatch = isAnyPartTypeSet && oldPartType === newPartType;
            const isPartDescriptorMatch =
              isAnyDescriptorSet && oldPartDescriptor === newPartDescriptor;
            const isPointOnSamePartType =
              isPartDescriptorMatch ||
              isPartTypeMatch ||
              (!isAnyPartTypeSet && !isAnyDescriptorSet);

            console.log(logPrefix, "onPointMoved", {
              params,
              oldPartType,
              oldPartDescriptor,
              newPartType,
              newPartDescriptor,
              isAnyPartTypeSet,
              isAnyDescriptorSet,
              isPartTypeMatch,
              isPartDescriptorMatch,
              isPointOnSamePartType,
            });

            if (isPointOnSamePartType) {
              const newDamage: TDamage = {
                ...params.point.data,
                point: params.endCoords,
                svgPathMetadata: params.svgPathMetadata,
                isRepositioningRequired: false,
              };
              onUpdated(newDamage);
              return;
            } else {
              // if point was dragged to a different vehicle part type - show update modal and force to select valid part type and damage type.
              let newDamage: TDamage = GenericDamageHelper.resetPartTypeAndDamageType(
                params.point.data,
              );
              newDamage = {
                ...newDamage,
                point: params.endCoords,
                svgPathMetadata: params.svgPathMetadata,
                isRepositioningRequired: false,
              };

              // use the most fresh damage if it was changed in the callback,
              // to handle edge case when damage is changed in the callback,
              // but we already opened the modal for the prev damage state, and after save we would override the changes
              const callbackResult = onUpdated(newDamage);
              const newDamage2 =
                callbackResult instanceof Object ? callbackResult.newValue : newDamage;

              setPartTypeExtraFilters(
                newDamage2.svgPathMetadata
                  ? {
                      damageId: newDamage2.id,
                      category: newDamage2.svgPathMetadata?.vehiclePartCategory || undefined,
                      types: newDamage2.svgPathMetadata?.vehiclePartTypes || undefined,
                      descriptors: newDamage2.svgPathMetadata?.vehiclePartDescriptors || undefined,
                    }
                  : undefined,
              );
              setDamageToUpdate(newDamage2);
              enqueueSnackbar(
                "You moved damage to a different vehicle part. Please review the changes.",
                {
                  variant: "info",
                },
              );
              return;
            }
          },
        }}
        secondaryViewerProps={{
          ...visualModelViewerProps?.secondaryViewerProps,
        }}
      />

      {/* View damages on different visual models */}
      {(() => {
        const canViewCurrent = TypeHelper.isNotEmptyArray(damagesOnForeignVisualModels);
        const canViewOld =
          isViewOldEnabled && TypeHelper.isNotEmptyArray(vehicleDamagesOnOtherVisualModels);

        return (
          <>
            {(canViewCurrent || canViewOld) && (
              <Stack direction={{ xxs: "column", md: "row" }} gap={1}>
                {/* View current damages on other visual models */}
                {canViewCurrent && (
                  <AppButton
                    sx={{ whiteSpace: { xxs: "unset", md: "nowrap" } }}
                    variant='outlined'
                    color='text'
                    size='small'
                    startIcon={<AppIcon of='view' />}
                    onClick={() => setIsViewDamagesOnOtherVisualModelsModalOpen(true)}
                  >
                    View {damagesOnForeignVisualModels?.length}{" "}
                    {TextHelper.pluralize("damage", damagesOnForeignVisualModels?.length)} on other
                    visual models
                  </AppButton>
                )}

                {/* View old damages on other visual models */}
                {canViewOld && (
                  <AppButton
                    sx={{ whiteSpace: { xxs: "unset", md: "nowrap" } }}
                    variant='outlined'
                    color='text'
                    size='small'
                    startIcon={<AppIcon of='view' />}
                    onClick={() => setIsViewOldDamagesOnOtherVisualModelsModalOpen(true)}
                  >
                    View {vehicleDamagesOnOtherVisualModels?.length} old{" "}
                    {TextHelper.pluralize("damage", vehicleDamagesOnOtherVisualModels?.length)} on
                    other visual models
                  </AppButton>
                )}
              </Stack>
            )}
          </>
        );
      })()}

      {/* Damage list */}
      {isDisplayList && (
        <GenericDamageList
          damages={damages}
          isViewEnabled={isViewEnabled}
          isUpdateEnabled={isEditMode && isUpdateEnabled}
          isDeleteEnabled={isEditMode && isDeleteEnabled}
          withAlertsColumn
          errorsByDamageIdMap={errorsByDamageIdMap}
          onView={(damage) => {
            view(damage);
          }}
          onUpdate={(damage) => {
            setDamageToUpdate(damage);
          }}
          onDelete={(damage) => {
            setDamageToDelete(damage);
          }}
        />
      )}

      {/* Vehicle damage view modal */}
      {isViewOldEnabled && (
        <VehicleDamageViewModal
          open={!!oldDamageToView}
          onClose={() => {
            setOldDamageToView(null);
          }}
          viewProps={{
            vehicleId: oldDamageToView?.vehicle?.id || undefined,
            vehicleDamageId: oldDamageToView?.id,
            vehicleDamage: oldDamageToView,
            displayProps: {
              breadcrumbs: false,
              header: true,
              delete: false,
              damageDetection: false,
              visualModel: true,
            },
            onDelete: null,
          }}
        />
      )}

      {/* View damage modal */}
      {isViewEnabled && damageToView && (
        <GenericDamageViewModal
          open={!!damageToView}
          onClose={() => {
            setDamageToView(null);
          }}
          viewProps={{
            damage: damageToView,
          }}
        />
      )}

      {/* Create/update damage modal */}
      {isEditMode &&
        (isViewEnabled || isCreateEnabled || isUpdateEnabled || isDeleteEnabled) &&
        damageToCreateOrUpdate && (
          <VisualModelEditorOfGenericDamageInputModal
            open={!!damageToCreateOrUpdate}
            onClose={() => {
              setDamageToCreate(null);
              setDamageToUpdate(null);
            }}
            formMode={damageToCreate ? EntityFormMode.Create : EntityFormMode.Update}
            vehicleId={vehicleId}
            visualModel={visualModelComputed}
            damages={damages}
            damage={damageToCreateOrUpdate}
            isPreload={true}
            isUpdateEnabled={isUpdateEnabled}
            isDeleteEnabled={isDeleteEnabled}
            isSplitEnabled={isSplitEnabled}
            isMergeEnabled={isMergeEnabled}
            isPartTypeDisabled={createUpdateProps?.isPartTypeDisabled}
            isDamageTypeDisabled={createUpdateProps?.isDamageTypeDisabled}
            isSubmitDisabled={createUpdateProps?.isSubmitDisabled}
            partTypeExtraFilters={
              partTypeExtraFilters?.damageId === damageToCreateOrUpdate.id
                ? partTypeExtraFilters
                : undefined
            }
            errors={errorsByDamageIdMap[damageToCreateOrUpdate.id || ""]}
            onCreated={(newValue) => {
              onCreated(newValue);
            }}
            onUpdated={(newValue) => {
              onUpdated(newValue);
            }}
            onDeleted={(newValue) => {
              setDamageToDelete(newValue);
            }}
            onManyChanged={(newValue) => {
              onManyChanged(newValue);
            }}
            setPartTypeExtraFilters={setPartTypeExtraFilters}
          />
        )}

      {/* Delete damage modal */}
      {isEditMode && isDeleteEnabled && damageToDelete && (
        <ConfirmationModal
          keepMounted={false}
          open={!!damageToDelete}
          title='Delete damage?'
          onClose={() => {
            setDamageToDelete(null);
          }}
          onCancel={() => {
            setDamageToDelete(null);
          }}
          onConfirm={() => {
            onDeleted(damageToDelete);
            setDamageToDelete(null);
            setDamageToUpdate(null);
          }}
          body={
            <Stack spacing={1}>
              <Box>{`You're going to delete the damage.`}</Box>
              {deleteProps?.confirmationModal?.bodyAfter &&
                deleteProps?.confirmationModal?.bodyAfter(damageToDelete)}
            </Stack>
          }
        />
      )}

      {/* View current damages on different visual models modal */}
      <VisualModelViewerOfGenericDamagesModal
        open={isViewDamagesOnOtherVisualModelsModalOpen}
        onClose={() => {
          setIsViewDamagesOnOtherVisualModelsModalOpen(false);
        }}
        viewProps={{
          damages: damagesOnForeignVisualModels,
          withDamageList: true,
        }}
      />

      {/* View old vehicle damages on different visual models modal */}
      <VisualModelViewerOfVehicleDamagesModal
        open={isViewOldDamagesOnOtherVisualModelsModalOpen}
        onClose={() => {
          setIsViewOldDamagesOnOtherVisualModelsModalOpen(false);
        }}
        viewProps={{
          entities: vehicleDamagesOnOtherVisualModels,
          withDamageList: true,
          onUpdated: (newValue) => {
            newValue
              ? paginatedOldVehicleDamagesRequest.updateData((data) => {
                  data.items = data.items?.map((x) => (x.id == newValue.id ? newValue : x));
                })
              : paginatedOldVehicleDamagesRequest.refetch();
          },
          onDeleted: (newValue) => {
            newValue
              ? paginatedOldVehicleDamagesRequest.updateData((data) => {
                  data.items = data.items?.filter((x) => x.id != newValue.id);
                })
              : paginatedOldVehicleDamagesRequest.refetch();
          },
        }}
      />
    </Stack>
  );
}

export const VisualModelEditorOfGenericDamage = forwardRef(
  TheComponent,
) as TheComponentWithForwardedRefType;
